import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const messageHistorySlice = createSlice({
    name: 'messageHistory',
    initialState: {
        data: []
    },
    reducers: {
        addOrUpdate: (state, action) => {
            for (const msg of ensureArray(action.payload)) {
                let idx = state.data.findIndex(m => m.id === msg.id);
                if (idx >= 0) {
                    state.data.splice(idx, 1, msg);
                } else {
                    state.data.push(msg);
                }
            }
            state.data.sort((a, b) => {
                let aTime = new Date(a.timestamp);
                let bTime = new Date(b.timestamp);
                if (aTime < bTime) {
                    return -1;
                } else if (aTime > bTime) {
                    return 1;
                }
                return 0;
            }) 
        },
        remove: (state, action) => {
            const toRemove = new Set(ensureArray(action.payload).map(message => message.id));
            state.data = state.data.filter(message => !toRemove.has(message.id));
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => messageHistorySlice.getInitialState());
    }
});

// #region network operations
const fetchMessage = id => network => network.get(`/api/v1/historicMessage/${encodeURIComponent(id)}`, messageHistorySlice.actions.addOrUpdate);
const fetchJobMessages = jobid => network => network.get(`/api/v1/jobMessageHistory/${encodeURIComponent(jobid)}`, messageHistorySlice.actions.addOrUpdate);
const fetchIncrement = (start, count) => network => network.get(`/api/v1/messageHistory/${encodeURIComponent(start)}/${encodeURIComponent(count)}`, messageHistorySlice.actions.addOrUpdate);
export const replyToMessage = (network, id, data) => network.post(`/api/v1/messageReply/${encodeURIComponent(id)}`, data, messageHistorySlice.actions.addOrUpdate); // TODO better failure action
export const crewSMSBroadcast = (network, crews, message) => network.post('/api/v1/sms/crews', {crews, message}, messageHistorySlice.actions.addOrUpdate); // TODO better failure action
export const contactSMSBroadcast = (network, contacts, message) => network.post('/api/v1/sms/contacts', {contacts, message}, messageHistorySlice.actions.addOrUpdate); // TODO better failure action
// #endregion

// #region selectors
const selectAllMessages = state => state.messageHistory.data;

const selectMessagesByJobID = createSelector([selectAllMessages, (messages, jobid) => jobid, (messages, jobid, origin) => origin], (messages, jobid, origin) => messages.filter(msg => msg.jobid && msg.jobid.toString() === jobid.toString() && msg.type === origin));
const selectJobMessages = (jobid, origin)  => state => selectMessagesByJobID(state, jobid, origin);

const selectMessagesByIncrement = createSelector([selectAllMessages, (messages, start) => start, (messages, start, count) => count], (messages, start, count) => {
    return messages.slice(start, start + count)
});
const selectMessageIncrement = (start, count) => state => selectMessagesByIncrement(state, start, count);

// #endregion

// #region listeners
const listen = {
    messagesent: (message, { dispatch }) => dispatch(messageHistorySlice.actions.addOrUpdate(message))
}
// #endregion 

const options = { cleanup: { root: 'messageHistory', all: selectAllMessages, removeAll: messageHistorySlice.actions.remove, remove: messageHistorySlice.actions.remove}}
export const [useJobMessageHistory] = dataHookFactory(selectJobMessages, initMapFactory(fetchJobMessages, listen), options);
export const [useMessages] = dataHookFactory(selectMessageIncrement, initMapFactory(fetchIncrement, listen), options);

const reducerDetails = { name: messageHistorySlice.name, reducer: messageHistorySlice.reducer };
export default reducerDetails;